import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import dashboardService from '../../services/dashboard.service';
import Loading from '../common/Loading';
const OVERVIEW_METRICS = [
    {
        title: 'Total Clinics',
        key: 'CLINICS',
        icon: 'bi-building',
        cardClass: 'admin-dashboard-stat--clinics',
        desc: 'All registered clinics',
    },
    {
        title: 'Active Clinics',
        key: 'ACTIVE_CLINICS',
        icon: 'bi-check-circle',
        cardClass: 'admin-dashboard-stat--active-clinics',
        desc: 'Currently active locations',
    },
    {
        title: 'Total Patients',
        key: 'PATIENTS',
        icon: 'bi-people',
        cardClass: 'admin-dashboard-stat--patients',
        desc: 'All registered patients',
    },
    {
        title: 'Active Reminders',
        key: 'APPOINTMENT_REMINDERS',
        icon: 'bi-alarm',
        cardClass: 'admin-dashboard-stat--reminders',
        desc: 'Pending reminders',
    },
];

const DETAIL_SECTIONS = [
    {
        id: 'clinic',
        title: 'Clinic Statistics',
        category: 'clinic',
        icon: 'bi-hospital',
        desc: 'Detailed clinic operations and status metrics',
        sectionClass: 'admin-dashboard-section--clinic',
    },
    {
        id: 'patient',
        title: 'Patient Statistics',
        category: 'patient',
        icon: 'bi-people-fill',
        desc: 'Patient demographics and engagement',
        sectionClass: 'admin-dashboard-section--patient',
    },
    {
        id: 'reminders',
        title: 'Reminders & Appointments',
        category: 'reminders',
        icon: 'bi-calendar-check',
        desc: 'All scheduled reminders and recalls',
        sectionClass: 'admin-dashboard-section--reminders',
    },
    {
        id: 'communications',
        title: 'Communications',
        category: 'communications',
        icon: 'bi-chat-left-text',
        desc: 'Messaging and notification statistics',
        sectionClass: 'admin-dashboard-section--communications',
    },
    {
        id: 'content',
        title: 'Content & Campaigns',
        category: 'content',
        icon: 'bi-file-earmark-text',
        desc: 'Articles, surveys and marketing campaigns',
        sectionClass: 'admin-dashboard-section--content',
    },
];

const OVERVIEW_KEYS = new Set(OVERVIEW_METRICS.map((metric) => metric.key));

function getCategoryStats(stats, category) {
    if (!Array.isArray(stats)) return [];

    return stats.filter((stat) => {
        if (!stat?.statsName || OVERVIEW_KEYS.has(stat.statsName)) return false;
        const name = stat.statsName.toLowerCase();

        switch (category) {
            case 'clinic':
                return name.includes('clinic') && !name.includes('patient');
            case 'patient':
                return name.includes('patient');
            case 'reminders':
                return (
                    name.includes('reminder') ||
                    name.includes('recall') ||
                    name.includes('appointment')
                );
            case 'communications':
                return (
                    (name.includes('sms') || name.includes('email') || name.includes('call')) &&
                    !name.includes('reminder')
                );
            case 'content':
                return (
                    name.includes('article') ||
                    name.includes('campaign') ||
                    name.includes('survey')
                );
            default:
                return false;
        }
    });
}

function getStatIcon(statName) {
    if (!statName) return 'bi-graph-up';
    if (statName.includes('ACTIVE')) return 'bi-check-circle';
    if (statName.includes('FAILED')) return 'bi-exclamation-triangle';
    if (statName.includes('SENT')) return 'bi-send-check';
    if (statName.includes('RECALL')) return 'bi-arrow-repeat';
    if (statName.includes('APPOINTMENT')) return 'bi-calendar-date';
    if (statName.includes('REMINDER')) return 'bi-alarm';
    if (statName.includes('SURVEY')) return 'bi-clipboard-data';
    if (statName.includes('ARTICLE')) return 'bi-journal-text';
    if (statName.includes('CAMPAIGN')) return 'bi-megaphone';
    if (statName.includes('SMS')) return 'bi-chat-dots';
    if (statName.includes('EMAIL')) return 'bi-envelope';
    if (statName.includes('CALL')) return 'bi-telephone';
    return 'bi-graph-up';
}

function formatName(name) {
    if (!name) return '';
    return name.replace(/_/g, ' ').replace(/(^\w|\s\w)/g, (m) => m.toUpperCase());
}

function isZeroValue(value) {
    return value === '0' || value === '0.00' || value === '0 (0%)' || value === 0;
}

function getValueColorClass(statName) {
    if (!statName) return '';
    if (statName.includes('FAILED')) return 'admin-dashboard-detail-card__value--danger';
    if (statName.includes('ACTIVE')) return 'admin-dashboard-detail-card__value--success';
    if (statName.includes('REMINDER') || statName.includes('RECALL') || statName.includes('APPOINTMENT')) {
        return 'admin-dashboard-detail-card__value--warning';
    }
    if (statName.includes('SURVEY') || statName.includes('ARTICLE') || statName.includes('CAMPAIGN')) {
        return 'admin-dashboard-detail-card__value--purple';
    }
    if (statName.includes('SMS') || statName.includes('EMAIL') || statName.includes('CALL')) {
        return 'admin-dashboard-detail-card__value--info';
    }
    if (statName.includes('PATIENT')) return 'admin-dashboard-detail-card__value--success';
    if (statName.includes('CLINIC')) return 'admin-dashboard-detail-card__value--primary';
    return '';
}

function DetailStatGrid({ stats }) {
    return (
        <div className="admin-dashboard-detail-grid">
            {stats.map((stat) => (
                <div key={stat.statsName} className="admin-dashboard-detail-card">
                    <div className="admin-dashboard-detail-card__name">
                        <i className={`bi ${getStatIcon(stat.statsName)}`}></i>
                        <span>{formatName(stat.statsName)}</span>
                    </div>
                    <div
                        className={`admin-dashboard-detail-card__value ${getValueColorClass(stat.statsName)} ${
                            isZeroValue(stat.statsValue) ? 'admin-dashboard-detail-card__value--zero' : ''
                        }`}
                    >
                        {stat.statsValue ?? '—'}
                    </div>
                    {stat.modifiedAt && (
                        <div className="admin-dashboard-detail-card__updated">
                            <i className="bi bi-clock me-1"></i>
                            {stat.modifiedAt}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

const Dashboard = () => {
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await dashboardService.getStats();
            setStats(Array.isArray(response?.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching stats:', err);
            setError('Failed to load dashboard data.');
            setStats([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const sectionsWithData = useMemo(
        () =>
            DETAIL_SECTIONS.map((section) => ({
                ...section,
                stats: getCategoryStats(stats, section.category),
            })).filter((section) => section.stats.length > 0),
        [stats]
    );

    const latestUpdated = useMemo(() => {
        const timestamps = stats.map((stat) => stat.modifiedAt).filter(Boolean);
        return timestamps[0] || null;
    }, [stats]);

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="pagetitle">
                    <h1>
                        <i className="bi bi-speedometer2"></i> Dashboard
                    </h1>
                </div>
                <Loading />
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="pagetitle d-flex justify-content-between align-items-center">
                <h1>
                    <i className="bi bi-speedometer2"></i> Dashboard
                </h1>
            </div>

            <Card className="admin-dashboard-card shadow-sm border-0">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
                        <div>
                            <h5 className="admin-dashboard-card__title mb-1">Admin Overview</h5>
                            <p className="text-muted small mb-0">
                                Overview of clinic activities, patients, reminders, communications, and content.
                            </p>
                        </div>
                        <Button variant="outline-primary" size="sm" onClick={fetchStats} disabled={loading}>
                            <i className={`bi bi-arrow-clockwise me-1 ${loading ? 'admin-dashboard-spin' : ''}`}></i>
                            Refresh
                        </Button>
                    </div>

                    {error && (
                        <Alert variant="warning" className="border-0 shadow-sm mb-3">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    )}

                    {!error && stats.length === 0 ? (
                        <div className="admin-dashboard-empty">
                            <i className="bi bi-database-exclamation"></i>
                            <h5>No data available</h5>
                            <p className="mb-3">Dashboard statistics will appear here once data is available.</p>
                            <Button variant="primary" size="sm" onClick={fetchStats}>
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Refresh Data
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="admin-dashboard-overview-grid">
                                {OVERVIEW_METRICS.map((metric) => {
                                    const stat = stats.find((item) => item.statsName === metric.key);
                                    return (
                                        <div
                                            key={metric.key}
                                            className={`admin-dashboard-stat ${metric.cardClass}`}
                                        >
                                            <div>
                                                <div className="admin-dashboard-stat__label">{metric.title}</div>
                                                <div className="admin-dashboard-stat__value">
                                                    {stat?.statsValue ?? '0'}
                                                </div>
                                                <div className="admin-dashboard-stat__desc">{metric.desc}</div>
                                            </div>
                                            <span className="admin-dashboard-stat__icon">
                                                <i className={`bi ${metric.icon}`}></i>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {latestUpdated && (
                                <div className="admin-dashboard-alert-banner">
                                    <i className="bi bi-info-circle"></i>
                                    <span>
                                        Statistics reflect the latest platform snapshot. Last updated:{' '}
                                        {latestUpdated}
                                    </span>
                                </div>
                            )}

                            {sectionsWithData.map((section) => (
                                <section
                                    key={section.id}
                                    className={`admin-dashboard-section ${section.sectionClass}`}
                                >
                                    <div className="admin-dashboard-section__header">
                                        <span className="admin-dashboard-section__icon">
                                            <i className={`bi ${section.icon}`}></i>
                                        </span>
                                        <div>
                                            <div>{section.title}</div>
                                            <small className="text-muted fw-normal">{section.desc}</small>
                                        </div>
                                        <span className="admin-dashboard-section__count">
                                            {section.stats.length} metric{section.stats.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <DetailStatGrid stats={section.stats} />
                                </section>
                            ))}
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default Dashboard;
